/*
 * Sport of Padel – Racket finder (tag based)
 *
 * Reads the rackets the section renders as JSON and ranks the in-stock ones by
 * the shopper's answers. Racket data comes from product tags:
 *
 *   Level-beginner | Level-intermediate | Level-advanced   (one or more)
 *   Style-control | Style-balanced | Style-power
 *   Shape-round | Shape-teardrop | Shape-diamond
 *   Weight-360            (grams)
 *   Balance-low | Balance-medium | Balance-high
 *   Feel-soft | Feel-medium | Feel-hard
 *   Feature-Comfort, Feature-Lightweight, Feature-Stability, Model-women
 *
 * Tags are matched case-insensitively, and Swedish/Norwegian values are understood.
 */
(() => {
  if (customElements.get('sop-racket-finder')) return;

  const ORDER = {
    level: ['beginner', 'intermediate', 'advanced'],
    style: ['control', 'balanced', 'power'],
    shape: ['round', 'teardrop', 'diamond'],
    balance: ['low', 'medium', 'high'],
    feel: ['soft', 'medium', 'hard'],
  };

  const ALIASES = {
    level: {
      beginner: ['beginner', 'nybörjare', 'nyborjare', 'nybegynner', 'begynner'],
      intermediate: ['intermediate', 'medel', 'middels', 'mellom'],
      advanced: ['advanced', 'avancerad', 'avansert', 'expert', 'pro'],
    },
    style: {
      control: ['control', 'kontroll'],
      balanced: ['balanced', 'allround', 'all-round', 'hybrid', 'balanserad', 'balansert'],
      power: ['power', 'kraft'],
    },
    shape: {
      round: ['round', 'rund'],
      teardrop: ['teardrop', 'tear-drop', 'droppe', 'dråpe', 'drape', 'hybrid'],
      diamond: ['diamond', 'diamant', 'geometric', 'geometrisk'],
    },
    balance: {
      low: ['low', 'låg', 'lav'],
      medium: ['medium', 'medel', 'middels', 'mid', 'neutral'],
      high: ['high', 'hög', 'høy', 'hoy'],
    },
    feel: {
      soft: ['soft', 'mjuk', 'myk'],
      medium: ['medium', 'medel', 'middels'],
      hard: ['hard', 'hård'],
    },
  };

  /* How much each answer counts. */
  const WEIGHTS = { level: 3, style: 3, feel: 1.5, arm: 2, light: 1.5, women: 1.5, stability: 1 };
  const MISSING_CREDIT = 0.35;
  const NEIGHBOUR_CREDIT = 0.4;
  /* "Not sure" on play style: suggest a style from the level, with less weight. */
  const STYLE_FROM_LEVEL = { beginner: 'control', intermediate: 'balanced', advanced: null };

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const lookup = (dimension, raw) => {
    const value = String(raw).trim().toLowerCase();
    for (const [key, words] of Object.entries(ALIASES[dimension])) {
      if (words.includes(value)) return key;
    }
    return null;
  };

  /* Turn a product's tags into finder data. */
  const parseTags = (tags) => {
    const data = { level: [], style: [], shape: [], balance: [], feel: [], grams: null, comfort: false, light: false, stable: false, women: false };
    for (const tag of tags || []) {
      const text = String(tag).trim();
      const dash = text.indexOf('-');
      if (dash < 1) continue;
      const prefix = text.slice(0, dash).toLowerCase();
      const value = text.slice(dash + 1).trim();
      const lower = value.toLowerCase();

      if (prefix === 'weight' || prefix === 'vikt' || prefix === 'vekt') {
        const grams = parseInt(lower.replace(/[^\d]/g, ''), 10);
        if (grams) data.grams = grams;
      } else if (prefix === 'feature' || prefix === 'egenskap') {
        if (['comfort', 'komfort', 'arm-friendly'].includes(lower)) data.comfort = true;
        if (['lightweight', 'lättvikt', 'lettvekt', 'light'].includes(lower)) data.light = true;
        if (['stability', 'stabilitet'].includes(lower)) data.stable = true;
      } else if (prefix === 'model' || prefix === 'modell') {
        if (['women', 'woman', 'dam', 'dame'].includes(lower)) data.women = true;
      } else {
        const dimension = { level: 'level', nivå: 'level', style: 'style', spelstil: 'style', spillestil: 'style', shape: 'shape', form: 'shape', balance: 'balance', balans: 'balance', feel: 'feel', känsla: 'feel', følelse: 'feel' }[prefix];
        if (!dimension) continue;
        const key = lookup(dimension, lower);
        if (key && !data[dimension].includes(key)) data[dimension].push(key);
      }
    }
    if (data.grams && data.grams <= 355) data.light = true;
    if (data.grams && data.grams >= 370) data.stable = true;
    return data;
  };

  /* 1 = exact, 0.4 = neighbour, 0 = opposite, null = missing */
  const closeness = (dimension, wanted, have) => {
    if (!have.length) return null;
    if (have.includes(wanted)) return 1;
    const order = ORDER[dimension];
    const distance = Math.min(...have.map((key) => Math.abs(order.indexOf(key) - order.indexOf(wanted))));
    return distance === 1 ? NEIGHBOUR_CREDIT : 0;
  };

  /* Words that don't identify a model family, so "XPLO 26", "XPLO 27" and "XPLO CMF 26" count as one family. */
  const FAMILY_NOISE = new Set([
    'pro', 'light', 'ls', 'lite', 'motion', 'team', 'w', 'woman', 'women', 'air', 'hybrid', 'geo', 'cmf', 'comfort',
    'edge', 'x', 'special', 'edition', 'legend', 'tour', 'court', 'one', 'pink', 'mint', 'green', '–', '-',
  ]);
  const familyOf = (product) => {
    const brand = String(product.vendor || '').toLowerCase();
    return String(product.title || '')
      .toLowerCase()
      .replace(/\+/g, ' ')
      .split(/\s+/)
      .filter((w) => w && w !== brand && !brand.split(/\s+/).includes(w) && !FAMILY_NOISE.has(w) && !/^\d{2}(\d{2})?$/.test(w) && !/^v\d$/.test(w) && !/^\d{4}\/\d{4}$/.test(w) && !/^\d\.\d$/.test(w))
      .join(' ');
  };

  class SopRacketFinder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';

      this.root = this.querySelector('[data-sop-finder-root]');
      this.t = this.readJson('[data-sop-finder-strings]', {});
      this.designMode = this.dataset.designMode === 'true';
      this.allUrl = this.dataset.allUrl || '/collections/all';

      try {
        this.money = new Intl.NumberFormat(this.dataset.locale || undefined, {
          style: 'currency',
          currency: this.dataset.currency || 'SEK',
          maximumFractionDigits: 0,
        });
      } catch (error) {
        this.money = { format: (v) => String(Math.round(v)) };
      }

      this.products = this.readJson('[data-sop-finder-products]', [])
        .filter((p) => p.available !== false && !/junior/i.test(`${p.type || ''} ${p.title || ''}`))
        .map((p) => ({ ...p, ...parseTags(p.tags), family: familyOf(p) }))
        .filter((p) => p.level.length || p.style.length || p.shape.length);

      if (!this.products.length) {
        if (this.designMode) this.root.innerHTML = `<p class="sop-finder__notice">${escapeHtml(this.t.no_data)}</p>`;
        else this.closest('.sop-finder')?.setAttribute('hidden', '');
        return;
      }

      this.vendors = [...new Set(this.products.map((p) => p.vendor).filter(Boolean))].sort((a, b) => a.localeCompare(b));
      this.budget = this.budgetBands();

      this.steps = ['level', 'style', 'feel', 'priorities'];
      if (this.budget) this.steps.push('budget');
      if (this.vendors.length > 1) this.steps.push('brand');

      this.root.addEventListener('click', (event) => this.onClick(event));
      this.reset();
    }

    readJson(selector, fallback) {
      try {
        return JSON.parse(this.querySelector(selector)?.textContent || '') ?? fallback;
      } catch (error) {
        return fallback;
      }
    }

    text(key, vars = {}) {
      let value = this.t[key] ?? key;
      for (const [name, replacement] of Object.entries(vars)) value = value.replace(`[${name}]`, replacement);
      return value;
    }

    /* Three price bands from the catalogue itself, rounded to tidy amounts in the shopper's currency. */
    budgetBands() {
      const prices = this.products.map((p) => p.price).filter((p) => p > 0).sort((a, b) => a - b);
      if (prices.length < 6) return null;
      const at = (q) => prices[Math.min(prices.length - 1, Math.floor(q * prices.length))];
      const tidy = (cents) => {
        const step = Math.pow(10, Math.max(2, Math.floor(Math.log10(cents)) - 1));
        return Math.round(cents / step) * step;
      };
      const low = tidy(at(1 / 3));
      const high = tidy(at(2 / 3));
      if (!(low < high)) return null;
      return { low, high };
    }

    reset() {
      this.answers = { level: null, style: null, feel: null, priorities: [], budget: null, brand: [] };
      this.stepIndex = 0;
      this.visible = 3;
      this.renderStep(false);
    }

    /* ---------- Questions ---------- */

    optionsFor(stepId) {
      const option = (value, labelKey, descKey, vars) => ({
        value,
        label: this.text(labelKey, vars),
        desc: descKey ? this.text(descKey, vars) : '',
      });

      switch (stepId) {
        case 'level':
          return ORDER.level.map((k) => option(k, `level_${k}_label`, `level_${k}_desc`));
        case 'style':
          return [...ORDER.style.map((k) => option(k, `style_${k}_label`, `style_${k}_desc`)), option('unsure', 'style_unsure_label', 'style_unsure_desc')];
        case 'feel':
          return [...ORDER.feel.map((k) => option(k, `feel_${k}_label`, `feel_${k}_desc`)), option('unsure', 'feel_unsure_label', 'feel_unsure_desc')];
        case 'priorities':
          return ['arm', 'light', 'women', 'stability'].map((k) => option(k, `prio_${k}_label`, `prio_${k}_desc`));
        case 'budget': {
          const vars = { low: this.money.format(this.budget.low / 100), high: this.money.format(this.budget.high / 100) };
          return ['low', 'mid', 'high', 'any'].map((k) => option(k, `budget_${k}_label`, `budget_${k}_desc`, vars));
        }
        case 'brand':
          return this.vendors.map((v) => ({ value: v, label: v, desc: '' }));
        default:
          return [];
      }
    }

    isMulti(stepId) {
      return stepId === 'priorities' || stepId === 'brand';
    }

    isSelected(stepId, value) {
      return this.isMulti(stepId) ? this.answers[stepId].includes(value) : this.answers[stepId] === value;
    }

    renderStep(moveFocus = true) {
      const stepId = this.steps[this.stepIndex];
      const total = this.steps.length;
      const multi = this.isMulti(stepId);
      const isLast = this.stepIndex === total - 1;

      const buttons = this.optionsFor(stepId)
        .map(
          (o) => `
          <button type="button" class="sop-finder__option" data-value="${escapeHtml(o.value)}" aria-pressed="${this.isSelected(stepId, o.value)}">
            <span class="sop-finder__option-label">${escapeHtml(o.label)}</span>
            ${o.desc ? `<span class="sop-finder__option-desc">${escapeHtml(o.desc)}</span>` : ''}
          </button>`
        )
        .join('');

      const noneKey = stepId === 'brand' ? 'any_brand' : 'prio_none_label';
      const none = multi
        ? `<button type="button" class="sop-finder__option" data-value="__none" aria-pressed="${this.answers[stepId].length === 0}">
             <span class="sop-finder__option-label">${escapeHtml(this.text(noneKey))}</span>
           </button>`
        : '';

      const desc = this.t[`${stepId}_desc`];

      this.root.innerHTML = `
        <div class="sop-finder__progress" aria-hidden="true">
          <span>${this.stepIndex + 1} / ${total}</span>
          <span class="sop-finder__bar"><span style="width:${((this.stepIndex + 1) / total) * 100}%"></span></span>
        </div>
        <fieldset class="sop-finder__step">
          <legend class="sop-finder__question" tabindex="-1">${escapeHtml(this.text(`${stepId}_title`))}</legend>
          ${desc ? `<p class="sop-finder__step-desc">${escapeHtml(desc)}</p>` : ''}
          <div class="sop-finder__options${stepId === 'brand' ? ' sop-finder__options--compact' : ''}">${none}${buttons}</div>
        </fieldset>
        <div class="sop-finder__nav">
          ${this.stepIndex > 0 ? `<button type="button" class="sop-finder__link-button" data-action="back">${escapeHtml(this.text('back'))}</button>` : '<span></span>'}
          ${multi ? `<button type="button" class="sop-finder__button" data-action="${isLast ? 'results' : 'next'}">${escapeHtml(this.text(isLast ? 'show_results' : 'next'))}</button>` : ''}
        </div>`;

      if (moveFocus) this.root.querySelector('.sop-finder__question')?.focus({ preventScroll: true });
    }

    onClick(event) {
      const option = event.target.closest('.sop-finder__option');
      const action = event.target.closest('[data-action]')?.dataset.action;
      const stepId = this.steps[this.stepIndex];

      if (option) {
        const value = option.dataset.value;
        if (this.isMulti(stepId)) {
          const list = this.answers[stepId];
          if (value === '__none') this.answers[stepId] = [];
          else this.answers[stepId] = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
          this.renderStep(false);
          this.root.querySelector(`.sop-finder__option[data-value="${CSS.escape(value)}"]`)?.focus();
          return;
        }
        this.answers[stepId] = value;
        this.root.querySelectorAll('.sop-finder__option').forEach((el) => el.setAttribute('aria-pressed', String(el === option)));
        window.setTimeout(() => this.next(), 180);
        return;
      }

      if (action === 'back') {
        this.stepIndex = Math.max(0, this.stepIndex - 1);
        this.renderStep();
      } else if (action === 'next') {
        this.next();
      } else if (action === 'results') {
        this.renderResults();
      } else if (action === 'more') {
        this.visible += 3;
        this.renderResults(false);
      } else if (action === 'restart') {
        this.reset();
        if (this.getBoundingClientRect().top < 0) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.root.querySelector('.sop-finder__question')?.focus({ preventScroll: true });
      }
    }

    next() {
      if (this.stepIndex < this.steps.length - 1) {
        this.stepIndex += 1;
        this.renderStep();
      } else {
        this.renderResults();
      }
    }

    /* ---------- Ranking ---------- */

    inBudget(product) {
      const band = this.answers.budget;
      if (!this.budget || !band || band === 'any' || !product.price) return true;
      if (band === 'low') return product.price < this.budget.low;
      if (band === 'mid') return product.price >= this.budget.low && product.price <= this.budget.high;
      return product.price > this.budget.high;
    }

    score(product) {
      const a = this.answers;
      let score = 0;
      let max = 0;
      const reasons = [];
      const add = (key, result) => {
        max += WEIGHTS[key];
        score += WEIGHTS[key] * (result == null ? MISSING_CREDIT : result);
      };

      // Level
      const level = closeness('level', a.level, product.level);
      add('level', level);
      if (level === 1) reasons.push(this.text('reason_level'));

      // Play style (or a suggestion from the level when the shopper is unsure)
      const style = a.style === 'unsure' ? STYLE_FROM_LEVEL[a.level] : a.style;
      if (style) {
        const result = closeness('style', style, product.style);
        if (a.style === 'unsure') {
          max += WEIGHTS.style / 2;
          score += (WEIGHTS.style / 2) * (result == null ? MISSING_CREDIT : result);
        } else {
          add('style', result);
        }
        if (result === 1) reasons.push(this.text(`reason_style_${style}`));
      }

      // Feel on impact (soft / medium / hard)
      let feelMatched = false;
      if (a.feel && a.feel !== 'unsure') {
        const result = closeness('feel', a.feel, product.feel);
        add('feel', result);
        if (result === 1) feelMatched = true;
      }

      // Priorities
      if (a.priorities.includes('arm')) {
        let result = product.comfort ? 1 : { soft: 0.8, medium: 0.4, hard: 0 }[product.feel[0]] ?? null;
        if (result != null && product.grams >= 370) result = Math.max(0, result - 0.3);
        add('arm', result);
        if (result >= 0.8) reasons.push(this.text('reason_arm'));
      }
      if (a.priorities.includes('light')) {
        const result = product.light ? 1 : product.grams ? (product.grams <= 360 ? 0.5 : 0) : null;
        add('light', result);
        if (result === 1) reasons.push(this.text('reason_light', { weight: product.grams || '' }));
      }
      if (a.priorities.includes('women')) {
        const result = product.women ? 1 : product.light ? 0.5 : 0;
        add('women', result);
        if (result === 1) reasons.push(this.text('reason_women'));
      }
      if (a.priorities.includes('stability')) {
        const result = product.stable ? 1 : product.grams ? (product.grams >= 365 ? 0.5 : 0) : null;
        add('stability', result);
        if (result === 1) reasons.push(this.text('reason_stable', { weight: product.grams || '' }));
      }

      const armReason = this.text('reason_arm');
      if (feelMatched && !reasons.includes(armReason)) {
        reasons.push(this.text('reason_feel', { feel: this.text(`feel_${a.feel}`) }));
      }

      if (this.answers.budget && this.answers.budget !== 'any' && this.inBudget(product) && product.price) {
        reasons.push(this.text('reason_budget'));
      }

      return { percent: max ? Math.round((score / max) * 100) : 0, reasons: reasons.slice(0, 3) };
    }

    rank() {
      const a = this.answers;
      const scored = this.products
        .map((product, position) => ({ product, position, ...this.score(product) }))
        .sort((x, y) => y.percent - x.percent || x.position - y.position);

      const fits = (item) => this.inBudget(item.product) && (!a.brand.length || a.brand.includes(item.product.vendor));
      const inside = scored.filter(fits);

      // Fewer than three that fit the budget/brand: fill up with the closest others, clearly marked.
      const outside = inside.length < 3 ? scored.filter((item) => !fits(item)).map((item) => ({ ...item, outside: true })) : [];
      return [...inside, ...outside.slice(0, 3 - inside.length)];
    }

    /* Best match, an alternative from another model family, and a cheaper option that still fits well. */
    pickTopThree(ranked) {
      const picks = [];
      const used = new Set();
      const take = (item, role) => {
        picks.push({ ...item, role });
        used.add(item.product.family);
      };
      const free = (item) => !used.has(item.product.family) && !picks.includes(item);

      if (ranked[0]) take(ranked[0], 'best');
      const second = ranked.find((item) => free(item) && !item.outside);
      if (second) take(second, 'alternative');

      const priceCap = Math.min(...picks.map((p) => p.product.price || Infinity));
      const value = ranked
        .filter((item) => free(item) && !item.outside && item.product.price && item.product.price < priceCap * 0.9 && item.percent >= (picks[0]?.percent || 0) - 15)
        .sort((x, y) => y.percent - x.percent || x.product.price - y.product.price)[0];
      if (value) take(value, 'value');
      else {
        const third = ranked.find((item) => free(item));
        if (third) take(third, 'alternative');
      }

      for (const item of ranked) {
        if (picks.length >= 3) break;
        if (!picks.some((p) => p.product === item.product)) picks.push({ ...item, role: null });
      }

      const rest = ranked.filter((item) => !picks.some((p) => p.product === item.product));
      return [...picks, ...rest];
    }

    renderCard(item) {
      const { product, percent, reasons, role, outside } = item;
      const chips = [];
      if (product.shape[0]) chips.push(this.text(`shape_${product.shape[0]}`));
      if (product.style[0]) chips.push(this.text(`style_${product.style[0]}_label`));
      if (product.grams) chips.push(`${product.grams} g`);
      if (product.feel[0]) chips.push(this.text(`feel_${product.feel[0]}`));

      const badge = outside ? this.text('outside') : role ? this.text(`role_${role}`) : '';
      const onSale = product.compare_at && product.compare_at > product.price;

      return `
        <li class="sop-finder__card">
          <a class="sop-finder__media" href="${escapeHtml(product.url)}" tabindex="-1" aria-hidden="true">
            ${product.image ? `<img src="${escapeHtml(product.image)}" alt="" loading="lazy" width="600" height="600">` : ''}
            ${badge ? `<span class="sop-finder__best${role === 'best' && !outside ? '' : ' sop-finder__best--quiet'}">${escapeHtml(badge)}</span>` : ''}
          </a>
          <div class="sop-finder__card-body">
            <p class="sop-finder__vendor">${escapeHtml(product.vendor)}</p>
            <h4 class="sop-finder__title"><a href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a></h4>
            <p class="sop-finder__price">
              ${escapeHtml(product.price_text || '')}
              ${onSale ? `<s class="sop-finder__compare">${escapeHtml(product.compare_text || '')}</s>` : ''}
            </p>
            <p class="sop-finder__match">${escapeHtml(this.text('match', { percent }))}</p>
            ${reasons.length ? `<ul class="sop-finder__reasons">${reasons.map((r) => `<li>${escapeHtml(r)}</li>`).join('')}</ul>` : ''}
            <ul class="sop-finder__tags">${chips.map((c) => `<li>${escapeHtml(c)}</li>`).join('')}</ul>
          </div>
        </li>`;
    }

    renderResults(moveFocus = true) {
      const ranked = this.pickTopThree(this.rank());
      const shown = ranked.slice(0, this.visible);

      this.root.innerHTML = `
        <div class="sop-finder__results">
          <div class="sop-finder__results-head">
            <h3 class="sop-finder__question" tabindex="-1">${escapeHtml(this.text('results_title'))}</h3>
            <p class="sop-finder__step-desc">${escapeHtml(this.text(ranked.length ? 'results_intro' : 'empty'))}</p>
          </div>
          ${shown.length ? `<ul class="sop-finder__grid">${shown.map((item) => this.renderCard(item)).join('')}</ul>` : ''}
          <div class="sop-finder__nav sop-finder__nav--results">
            <button type="button" class="sop-finder__link-button" data-action="restart">${escapeHtml(this.text('restart'))}</button>
            <span class="sop-finder__nav-end">
              ${ranked.length > this.visible ? `<button type="button" class="sop-finder__button sop-finder__button--outline" data-action="more">${escapeHtml(this.text('show_more'))}</button>` : ''}
              <a class="sop-finder__link-button" href="${escapeHtml(this.allUrl)}">${escapeHtml(this.text('all_rackets'))}</a>
            </span>
          </div>
        </div>`;

      if (moveFocus) {
        if (this.getBoundingClientRect().top < 0) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.root.querySelector('.sop-finder__question')?.focus({ preventScroll: true });
      }
    }
  }

  customElements.define('sop-racket-finder', SopRacketFinder);
})();
